import { Injectable } from '@angular/core';

const URL_MAPPER = {
  'https://api.inercomp.com/v1': '',
  'https://api-staging.inercomp.com/v1': '_STAGING',
  'https://emsa.icservice.inercomp.com/v1': '_DEV',
  'https://api-testing.inercomp.com/v1': '_TEST',
} as const;

interface DBConfig {
  name: string;
  version: number;
  stores: {
    [key: string]: {
      keyPath: string;
      indexes?: { name: string; keyPath: string }[];
    };
  };
}

@Injectable()
export class IndexedDBService {
  private dbs: Record<string, IDBDatabase> = {}
  private dbConfig: DBConfig = {
    name: 'FeederDB',
    version: 1,
    stores: {
      historicPrices: { keyPath: 'id' },
      livePrices: { keyPath: 'id' },
      timeSeries: { keyPath: 'id' },
      marketAreas: { keyPath: 'id' },
      priceCategories: { keyPath: 'name' },
    },
  };

  async initDatabase(): Promise<void> {
    const env = URL_MAPPER[window.api_url];
    if (this.dbs[env]) {
      return Promise.resolve();
    }
    try {
      this.dbs[env] = await this.openDatabase();
    } catch (error) {
      console.error('Failed to initialize database:', error);
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    const env = URL_MAPPER[window.api_url];
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(`${this.dbConfig.name}${env}`, this.dbConfig.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        Object.entries(this.dbConfig.stores).forEach(
          ([storeName, storeConfig]) => {
            if (!db.objectStoreNames.contains(storeName)) {
              const store = db.createObjectStore(storeName, {
                keyPath: storeConfig.keyPath,
              });

              if (storeConfig.indexes) {
                storeConfig.indexes.forEach((index) => {
                  store.createIndex(index.name, index.keyPath);
                });
              }
            }
          }
        );
      };
    });
  }

  async save<T>(storeName: string, data: T): Promise<void> {
    return this.performTransaction(storeName, 'readwrite', (store) => {
      store.put(data);
    });
  }

  async saveMany<T>(storeName: string, items: T[]): Promise<void> {
    return this.performTransaction<void>(storeName, 'readwrite', (store) => {
      for (const item of items) {
        store.put(item);
      }
    });
  }

  async get<T>(storeName: string, key: string): Promise<T | undefined> {
    return this.performTransaction(storeName, 'readonly', (store) => {
      return store.get(key);
    });
  }

  async getAll<T>(storeName: string): Promise<T[]> {
    return this.performTransaction(storeName, 'readonly', (store) => {
      return store.getAll();
    });
  }

  async delete(storeName: string, key: string): Promise<void> {
    return this.performTransaction(storeName, 'readwrite', (store) => {
      store.delete(key);
    });
  }

  async clear(storeName: string): Promise<void> {
    return this.performTransaction(storeName, 'readwrite', (store) => {
      store.clear();
    });
  }

  private async performTransaction<T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => void | IDBRequest
  ): Promise<T> {
    const db = this.dbs[URL_MAPPER[window.api_url]];
    return new Promise((resolve, reject) => {
      if (!db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);

      transaction.onerror = () => reject(transaction.error);

      const request = operation(store) as IDBRequest;

      if (request) {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } else {
        transaction.oncomplete = () => resolve({} as T);
      }
    });
  }
}
